import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Languages, FolderOpen, Settings as SettingsIcon, Plus, Edit2, Trash2, Loader2, Save, Sun, Moon, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { t, language, setLanguage, translations, setTranslations } = useLanguage();
  const { theme, setTheme, isDark } = useTheme();
  const { isSuperadmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [localTranslations, setLocalTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name_nl: '', name_de: '', icon: 'folder' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, transRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/translations`)
      ]);
      setCategories(catRes.data);
      setLocalTranslations(transRes.data);
    } catch (e) {
      console.error('Failed to fetch settings:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTranslations = async () => {
    setSaving(true);
    try {
      const translationsList = Object.entries(localTranslations).map(([key, value]) => ({
        key,
        nl: value.nl,
        de: value.de
      }));
      await axios.post(`${API}/translations/bulk`, translationsList);
      setTranslations(localTranslations);
      toast.success(language === 'nl' ? 'Vertalingen opgeslagen' : language === 'de' ? 'Übersetzungen gespeichert' : 'Translations saved');
    } catch (e) {
      toast.error(language === 'nl' ? 'Opslaan mislukt' : language === 'de' ? 'Speichern fehlgeschlagen' : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm);
        toast.success(language === 'nl' ? 'Categorie bijgewerkt' : language === 'de' ? 'Kategorie aktualisiert' : 'Category updated');
      } else {
        await axios.post(`${API}/categories`, categoryForm);
        toast.success(language === 'nl' ? 'Categorie aangemaakt' : language === 'de' ? 'Kategorie erstellt' : 'Category created');
      }
      setCategoryOpen(false);
      setCategoryForm({ name_nl: '', name_de: '', icon: 'folder' });
      setEditingCategory(null);
      fetchData();
    } catch (e) {
      toast.error(language === 'nl' ? 'Actie mislukt' : language === 'de' ? 'Aktion fehlgeschlagen' : 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm(language === 'nl' ? 'Weet je zeker dat je deze categorie wilt verwijderen?' : language === 'de' ? 'Sind Sie sicher, dass Sie diese Kategorie löschen möchten?' : 'Are you sure you want to delete this category?')) {
      return;
    }
    try {
      await axios.delete(`${API}/categories/${catId}`);
      toast.success(language === 'nl' ? 'Categorie verwijderd' : language === 'de' ? 'Kategorie gelöscht' : 'Category deleted');
      fetchData();
    } catch (e) {
      toast.error(language === 'nl' ? 'Verwijderen mislukt' : language === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed');
    }
  };

  const updateTranslation = (key, lang, value) => {
    setLocalTranslations(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [lang]: value
      }
    }));
  };

  const iconOptions = [
    { value: 'folder', label: 'Folder' },
    { value: 'book', label: 'Book' },
    { value: 'wrench', label: 'Wrench' },
    { value: 'graduation-cap', label: 'Graduation Cap' },
    { value: 'file', label: 'File' },
    { value: 'cog', label: 'Cog' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <h1 className="text-3xl font-bold text-white">{t('nav.settings')}</h1>

      <Tabs defaultValue="preferences" className="space-y-4">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="preferences" className="data-[state=active]:bg-accent" data-testid="tab-preferences">
            <User className="w-4 h-4 mr-2" />
            {language === 'nl' ? 'Voorkeuren' : language === 'de' ? 'Einstellungen' : 'Preferences'}
          </TabsTrigger>
          {isSuperadmin && (
            <>
              <TabsTrigger value="translations" className="data-[state=active]:bg-accent" data-testid="tab-translations">
                <Languages className="w-4 h-4 mr-2" />
                {t('settings.translations')}
              </TabsTrigger>
              <TabsTrigger value="categories" className="data-[state=active]:bg-accent" data-testid="tab-categories">
                <FolderOpen className="w-4 h-4 mr-2" />
                {t('settings.categories')}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-foreground">
                {language === 'nl' ? 'Persoonlijke voorkeuren' : language === 'de' ? 'Persönliche Einstellungen' : 'Personal Preferences'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Language Selection */}
              <div className="space-y-3">
                <Label className="text-foreground font-medium">
                  {language === 'nl' ? 'Taal' : language === 'de' ? 'Sprache' : 'Language'}
                </Label>
                <div className="flex gap-3">
                  <Button
                    variant={language === 'nl' ? 'default' : 'outline'}
                    onClick={() => setLanguage('nl')}
                    className={language === 'nl' ? 'bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-accent'}
                    data-testid="lang-nl"
                  >
                    🇳🇱 Nederlands
                  </Button>
                  <Button
                    variant={language === 'de' ? 'default' : 'outline'}
                    onClick={() => setLanguage('de')}
                    className={language === 'de' ? 'bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-accent'}
                    data-testid="lang-de"
                  >
                    🇩🇪 Deutsch
                  </Button>
                  <Button
                    variant={language === 'en' ? 'default' : 'outline'}
                    onClick={() => setLanguage('en')}
                    className={language === 'en' ? 'bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-accent'}
                    data-testid="lang-en"
                  >
                    🇬🇧 English
                  </Button>
                </div>
              </div>

              {/* Theme Selection */}
              <div className="space-y-3">
                <Label className="text-foreground font-medium">
                  {language === 'nl' ? 'Weergave' : language === 'de' ? 'Darstellung' : 'Appearance'}
                </Label>
                <div className="flex gap-3">
                  <Button
                    variant={isDark ? 'default' : 'outline'}
                    onClick={() => setTheme('dark')}
                    className={isDark ? 'bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-accent'}
                    data-testid="theme-dark"
                  >
                    <Moon className="w-4 h-4 mr-2" />
                    {language === 'nl' ? 'Donker' : language === 'de' ? 'Dunkel' : 'Dark'}
                  </Button>
                  <Button
                    variant={!isDark ? 'default' : 'outline'}
                    onClick={() => setTheme('light')}
                    className={!isDark ? 'bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-accent'}
                    data-testid="theme-light"
                  >
                    <Sun className="w-4 h-4 mr-2" />
                    {language === 'nl' ? 'Licht' : language === 'de' ? 'Hell' : 'Light'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Translations Tab */}
        <TabsContent value="translations">
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground">{t('settings.translations')}</CardTitle>
                <Button 
                  onClick={handleSaveTranslations} 
                  className="bg-primary hover:bg-primary/90"
                  disabled={saving}
                  data-testid="save-translations-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t('common.save')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {Object.entries(localTranslations).map(([key, value]) => (
                  <div key={key} className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`translation-${key}`}>
                    <div className="flex items-center">
                      <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded font-mono">
                        {key}
                      </code>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs mb-1 block">NL</Label>
                      <Input
                        value={value.nl || ''}
                        onChange={(e) => updateTranslation(key, 'nl', e.target.value)}
                        className="bg-background border-border text-foreground text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs mb-1 block">DE</Label>
                      <Input
                        value={value.de || ''}
                        onChange={(e) => updateTranslation(key, 'de', e.target.value)}
                        className="bg-background border-border text-foreground text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs mb-1 block">EN</Label>
                      <Input
                        value={value.en || ''}
                        onChange={(e) => updateTranslation(key, 'en', e.target.value)}
                        className="bg-background border-border text-foreground text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground">{t('settings.categories')}</CardTitle>
                <Dialog open={categoryOpen} onOpenChange={(open) => {
                  setCategoryOpen(open);
                  if (!open) {
                    setEditingCategory(null);
                    setCategoryForm({ name_nl: '', name_de: '', icon: 'folder' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary hover:bg-primary/90" data-testid="add-category-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('common.add')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="text-foreground">
                        {editingCategory 
                          ? (language === 'nl' ? 'Categorie bewerken' : language === 'de' ? 'Kategorie bearbeiten' : 'Edit category')
                          : (language === 'nl' ? 'Nieuwe categorie' : language === 'de' ? 'Neue Kategorie' : 'New category')}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCategory} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-foreground">{language === 'nl' ? 'Naam (NL)' : 'Name (NL)'} *</Label>
                        <Input
                          value={categoryForm.name_nl}
                          onChange={(e) => setCategoryForm({ ...categoryForm, name_nl: e.target.value })}
                          className="bg-background border-border text-foreground"
                          required
                          data-testid="category-name-nl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">{language === 'nl' ? 'Naam (DE)' : 'Name (DE)'} *</Label>
                        <Input
                          value={categoryForm.name_de}
                          onChange={(e) => setCategoryForm({ ...categoryForm, name_de: e.target.value })}
                          className="bg-background border-border text-foreground"
                          required
                          data-testid="category-name-de"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Icon</Label>
                        <select
                          value={categoryForm.icon}
                          onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                          className="w-full bg-background border border-border text-foreground rounded-md px-3 py-2"
                          data-testid="category-icon"
                        >
                          {iconOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setCategoryOpen(false)} className="border-border text-foreground">
                          {t('common.cancel')}
                        </Button>
                        <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={saving} data-testid="category-submit">
                          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          {t('common.save')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {categories.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  {language === 'nl' ? 'Geen categorieën gevonden' : language === 'de' ? 'Keine Kategorien gefunden' : 'No categories found'}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {categories.map((cat) => (
                    <div key={cat.id} className="flex items-center gap-4 p-4 hover:bg-accent/50 transition-colors" data-testid={`category-row-${cat.id}`}>
                      <div className="p-2 rounded-lg bg-primary/10">
                        <FolderOpen className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-foreground font-medium">{cat.name_nl}</p>
                        <p className="text-muted-foreground text-sm">{cat.name_de}</p>
                      </div>
                      <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        {cat.icon}
                      </code>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            setEditingCategory(cat);
                            setCategoryForm({ name_nl: cat.name_nl, name_de: cat.name_de, icon: cat.icon });
                            setCategoryOpen(true);
                          }}
                          data-testid={`edit-category-${cat.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteCategory(cat.id)}
                          data-testid={`delete-category-${cat.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
